import { atom, selector } from 'recoil';

import {
  deviceListInterface,
  deviceInterface,
  filterStateInterface,
  deviceDataInterface,
} from '../interfaces/device_interfaces';
import dayjs, { Dayjs } from 'dayjs';
import { Order } from '../../utils/sorting_helpers';

export const deviceListStateAtom = atom<deviceListInterface>({
  key: 'deviceListState', // unique ID (with respect to other atoms/selectors)
  default: {
    devices: [],
    archivedDevices: [],
    search_filter: {
      latitude: null,
      longitude: null,
      distance: null,
      warning_level: null,
      start_index: null,
      end_index: null,
      labels: null,
      pinned: null,
    },
    has_client_identifier: false,
  }, // default value (aka initial value)
});

export const systemTypesAtom = atom({
  key: 'systemTypesAtom',
  default: new Set<string>(),
});

export const systemTypesSelector = selector({
  key: 'systemTypesSelector',
  get: ({ get }) => Array.from(get(systemTypesAtom)),
});

export const selectedDeviceStateAtom = atom<deviceInterface | null>({
  key: 'selectedDeviceState',
  default: null,
});

export const selectedDeviceDataAtom = atom<deviceDataInterface | null>({
  key: 'selectedDeviceData',
  default: null,
});

export const filteredDeviceDataSelector = selector<deviceDataInterface | null>({
  key: 'filteredDeviceDataSelector',
  get: ({ get }) => {
    const data = get(selectedDeviceDataAtom);
    const startDate = get(periodStartDateAtom);
    const endDate = get(periodEndDateAtom);
    const periodType = get(periodTypeAtom);

    if (!data) return null;
    if (periodType === 'all') return data;

    const startUnix = startDate.unix();
    const endUnix = endDate.unix();

    return {
      ...data,
      water_data: data.water_data.filter(
        d =>
          d.measurement_unix_timestamp >= startUnix &&
          d.measurement_unix_timestamp <= endUnix,
      ),
      rainfall_data: data.rainfall_data.filter(
        d =>
          d.measurement_unix_timestamp >= startUnix &&
          d.measurement_unix_timestamp <= endUnix,
      ),
    };
  },
});

export const defaultFilterState = {
  pinned: false,
  lowBattery: false,
  highWarningLevel: false,
  noMessages: false,
  highROC: false,
  blockage: false,
  searchText: '',
  labels: [],
};
export const filterStateAtom = atom<filterStateInterface>({
  key: 'filterStateAtom',
  default: defaultFilterState,
});

export const filteredDeviceListSelector = selector({
  key: 'filteredDeviceListSelector',
  get: ({ get }) => {
    const filter = get(filterStateAtom);
    const deviceList = get(deviceListStateAtom);
    const mode = get(deviceModeAtom);
    const devices =
      mode === 'archived' ? deviceList.archivedDevices : deviceList.devices;

    if (
      !filter.highWarningLevel &&
      !filter.labels.length &&
      !filter.lowBattery &&
      !filter.pinned &&
      !filter.searchText &&
      !filter.noMessages &&
      !filter.blockage
    ) {
      return { devices };
    }

    return {
      devices: devices.filter(device => {
        if (
          !(
            device.device_id
              .toLowerCase()
              .includes(filter.searchText.toLowerCase()) ||
            device.client_identifier
              ?.toLowerCase()
              .includes(filter.searchText.toLowerCase())
          )
        ) {
          return false;
        } else if (filter.pinned && !device.pinned) {
          return false;
        } else if (filter.lowBattery && device.battery_percentage >= 25) {
          return false;
        } else if (filter.highWarningLevel && device.warning_level < 10) {
          return false;
        } else if (filter.highROC && device.labels.includes('High ROC')) {
          return false;
        } else if (
          filter.noMessages &&
          !device.labels.includes('No messages')
        ) {
          return false;
        } else if (filter.blockage && !device.labels.includes('blockage')) {
          return false;
        } else {
          return filter.labels.every(label => device.labels.includes(label));
        }
      }),
    };
  },
});

export type DeviceHeader =
  | 'client_identifier'
  | 'device_id'
  | 'current_level_percentage'
  | 'labels'
  | 'pinned'
  | 'creation_date'
  | 'end_date';
type DeviceSortingState = {
  sortBy: DeviceHeader;
  sortDirection: Order;
};
export const devicesSortingAtom = atom<DeviceSortingState>({
  key: 'devicesSortingAtom',
  default: {
    sortBy: 'device_id',
    sortDirection: 'asc',
  },
});

export const requestIDAtom = atom<number>({
  key: 'requestIDAtom',
  default: 0,
});

export const dateRangeAtom = atom<{ start: Dayjs; end: Dayjs }>({
  key: 'dateRangeAtom',
  default: {
    start: dayjs(0),
    end: dayjs(0),
  },
});

export const showClientIdentifierSelector = selector({
  key: 'showClientIdentifierSelector',
  get: ({ get }) => {
    const { has_client_identifier } = get(deviceListStateAtom);
    return has_client_identifier;
  },
});

export type Period = 'day' | 'week' | 'month' | 'year' | 'all' | 'custom';

export const periodTypeAtom = atom<Period>({
  key: 'periodType',
  default: 'all',
});

export const periodStartDateAtom = atom<Dayjs>({
  key: 'periodStartDate',
  default: dayjs().startOf('day'),
});

export const periodEndDateAtom = atom<Dayjs>({
  key: 'periodEndDate',
  default: dayjs().endOf('day'),
});

export const waterLevelFilteredAtom = atom<boolean>({
  key: 'waterLevelFilteredAtom',
  default: true,
});

export type DeviceMode = 'live' | 'archived';
export const deviceModeAtom = atom<DeviceMode>({
  key: 'deviceModeAtom',
  default: 'live',
});

// for downloading device data
export const selectedDevicesKeysAtom = atom<string[]>({
  key: 'selectedDevicesKeys',
  default: [],
});

export const downloadDeviceDataModeAtom = atom<'live' | 'all' | 'archived'>({
  key: 'downloadDeviceDataMode',
  default: 'live',
});
